import {
  Box,
  Button,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { green, red } from "@material-ui/core/colors";
import { Skeleton } from "@material-ui/lab";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { Check, X } from "react-feather";
import { useTranslation } from "react-i18next";
import { HistoryEntry } from "src/contexts/ApiContext";
import { useApi } from "src/hooks/useApi";
import useDatatype from "src/hooks/useDatatype";
import { Theme } from "src/theme";

import { downloadBlob } from "src/utils/downloadBlob";
import { getDateString } from "src/utils/timeDate";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    paddingTop: 38,
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    border: "none",
    [theme.breakpoints.up("sm")]: {
      borderLeft: "1px solid rgba(0,0,0,.1)",
    },
  },
  filename: {
    color: theme.palette.primary.main,
    textDecoration: "underline",
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.primary.light,
    },
  },
  selected: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.primary.main,
  },
  resize: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: 999,
    width: 24,
    cursor: "col-resize",
  },
  downloadall: {
    alignSelf: "flex-start",
    margin: "4.3rem 0 2rem 0",
  },
}));

interface Props {}

export const HistorySection: React.FC<Props> = () => {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historySize, setHistorySize] = useState(5);
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const { t } = useTranslation();
  const { datatype } = useDatatype();
  const { getHistory, downloadFile, downloadComplete } = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const [width, setWidth] = useState(400);

  useEffect(() => {
    if (datatype) {
      const get = async () => {
        try {
          setLoading(true);
          const h = await getHistory(datatype.id, historySize);
          /*setHistory(
            h.sort((a, b) => {
              return (
                (b.timestamp?.getDate() || 0) - (a.timestamp?.getDate() || 0)
              );
            })
          );*/
          setHistory(h);
          setLoading(false);
        } catch (e) {
          enqueueSnackbar("There has been an error fetching the history.");
          setLoading(false);
        }
      };

      get();
    }
    // eslint-disable-next-line
  }, [datatype, historySize]);

  const handleDownload = async (
    filename: string,
    contextId: string,
    requestId: string
  ) => {
    try {
      const blob = await downloadFile(contextId, requestId);
      downloadBlob(filename, blob);
    } catch (e) {
      enqueueSnackbar(t("views.history.downloadFileError"));
    }
  };
  const handleDownloadComplete = async (
    filename: string,
    contextId: string
  ) => {
    try {
      const blob = await downloadComplete(contextId);
      debugger;
      downloadBlob(filename, blob);
    } catch (e) {
      enqueueSnackbar(t("views.history.downloadAllError"));
    }
  };

  return (
    <Box className={classes.root}>
      <Box pt={1} display="flex" alignItems="flex-end">
        <Typography variant="h5" color="textSecondary">
          {t("views.history.title")}
        </Typography>

        <Typography
          variant="caption"
          color="textSecondary"
          style={{ marginLeft: 16, cursor: "pointer" }}
          onClick={() => setHistorySize(5)}
          className={historySize === 5 ? classes.selected : ""}
        >
          5
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          onClick={() => setHistorySize(10)}
          style={{ marginLeft: 8, cursor: "pointer" }}
          className={historySize === 10 ? classes.selected : ""}
        >
          10
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          onClick={() => setHistorySize(20)}
          style={{ marginLeft: 8, cursor: "pointer" }}
          className={historySize === 20 ? classes.selected : ""}
        >
          20
        </Typography>
      </Box>
      {datatype && (
        <Button
          size="small"
          variant="outlined"
          onClick={() =>
            handleDownloadComplete(datatype.displayName + ".csv", datatype.id)
          }
          className={classes.downloadall}
        >
          {t("views.history.downloadAll")}
        </Button>
      )}
      {!datatype ? (
        <Typography color="textSecondary" style={{ paddingTop: "4rem" }}>
          {t("views.history.selectContext")}
        </Typography>
      ) : loading ? (
        skeleton()
      ) : !history.length ? (
        <Typography color="textSecondary">
          {t("views.history.empty")}
        </Typography>
      ) : (
        history.map((d) => {
          const rejected = d?.rejectedLineCount;
          const accepted = d?.acceptedLineCount;
          const countries = d?.countries ? Object.keys(d.countries) : [];
          return (
            <Box display="flex" flexDirection="column" mb={6}>
              <Typography
                variant="body2"
                onClick={() => handleDownload(d.filename, datatype.id, d.id)}
                className={classes.filename}
              >
                {d.filename}
              </Typography>
              <Typography color="textSecondary" variant="body2">
                {d.user.firstName} {d.user.lastName},{" "}
                {d.timestamp ? getDateString(new Date(d.timestamp)) : "-"}
              </Typography>
              <Box display="flex">
                <Typography color="textSecondary" variant="body2">
                  {countries.join(", ")}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" pt={1} pb={2}>
                <>
                  <Tooltip title="Uploaded Datasets">
                    <Box
                      borderRadius="50%"
                      bgcolor={green[600]}
                      color="white"
                      mr={1}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width={14}
                      height={14}
                    >
                      <Check width={12} height={12} />
                    </Box>
                  </Tooltip>
                  <Typography style={{ marginRight: "1rem" }}>
                    {accepted}
                  </Typography>
                </>

                <>
                  <Tooltip title="Rejected Datasets">
                    <Box
                      borderRadius="50%"
                      bgcolor={red[600]}
                      color="white"
                      mr={1}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width={14}
                      height={14}
                    >
                      <X width={12} height={12} />
                    </Box>
                  </Tooltip>
                  <Typography style={{ marginRight: "1rem" }}>
                    {rejected}
                  </Typography>
                </>
              </Box>

              {/* <Box display="flex" alignItems="center" pt={1} pb={2}>
                <Label color="success">SUCCESS</Label>
                <Typography variant="body2" style={{ marginLeft: 8 }}>
                  {d.payload.length - 1} accepted
                </Typography>
              </Box> */}
              <Typography color="textSecondary" variant="body2">
                {d.comments ? d.comments[0] : ""}
              </Typography>
            </Box>
          );
        })
      )}
    </Box>
  );
};

const skeleton = () => {
  return [0, 1, 2, 3, 4].map(() => (
    <Box display="flex" flexDirection="column" mb={6}>
      <Skeleton variant="text" width={200} height={25} />
      <Skeleton variant="text" width={200} height={25} />
      <Skeleton variant="text" width={200} height={25} />
      <Skeleton variant="text" width={200} height={25} />
    </Box>
  ));
};
